import useCtcae, { ICtcaeIdentifier } from "@/hooks/data/useCtcae";
import { faInfoSquare } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, CircularProgress, Divider, IconButton, styled, Tooltip, Typography } from "@mui/material";
import { FC, useMemo } from "react";
import { Field, Format } from ".";


const CtcaeTooltip = styled(Box)(({ theme }) => ({
    margin: theme.spacing( -1),
}));

const CtcaeTooltipBox = styled(Box)(({ theme }) => ({
    margin: theme.spacing(0, -1),
    padding: theme.spacing(2, 3, 2, 3)
}));

const SourceDataCtcaeValue: FC<{formFieldValue?: string; oldValue?: boolean; newValue?: boolean;  noBox?: boolean}> = ({
    formFieldValue,
    oldValue,
    newValue,
    noBox
}) => {
    const ctcaeIdentifier = useMemo(() => {
        if (!formFieldValue) {
            return;
        }

        return JSON.parse(formFieldValue) as ICtcaeIdentifier;
    }, [formFieldValue]);

    const { data: ctcae, loading: ctcaeLoading } = useCtcae(ctcaeIdentifier); 
    
    return (
        <Field
            // @ts-ignore-next-line
            newValue={newValue ? "1" : undefined} 
            oldValue={oldValue ? "1" : undefined}
            noBox={noBox ? "1" : undefined}
        >
            <Format sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                {ctcaeLoading && <CircularProgress size="16px" />}
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis'}}>
                    {
                        ctcae && ctcaeIdentifier?.staticTerm && (
                            ctcae.grade ? `Grade ${ctcae.grade?.name}` : ' '
                        )
                    }
                    {
                        ctcae && !ctcaeIdentifier?.staticTerm && (
                            <>
                                {ctcae.term.name}{ctcae.grade ? `: Grade ${ctcae.grade?.name}` : null}
                            </>
                        )
                    }
                </Box>
                {
                    ctcae && (
                        <Tooltip 
                            title={
                                <CtcaeTooltip>
                                    <CtcaeTooltipBox>
                                        <Typography variant="subtitle1">
                                            Version
                                        </Typography>
                                        <Typography variant="caption">
                                            {ctcae.version.name}
                                        </Typography>
                                    </CtcaeTooltipBox>
                                    <Divider />
                                    <CtcaeTooltipBox>
                                        <Typography variant="subtitle1">
                                            Category
                                        </Typography>
                                        <Typography variant="caption">
                                            {ctcae.category.name}
                                        </Typography>
                                    </CtcaeTooltipBox>
                                    {
                                        ctcae.parentTerm && (
                                            <>
                                                <Divider />
                                                <CtcaeTooltipBox>
                                                    <Typography variant="subtitle1">
                                                        {ctcae.parentTerm.name}
                                                    </Typography>
                                                    <Typography variant="caption" component="div">
                                                        MedDRA Code: <em>{ctcae.parentTerm.medDraCode}</em>
                                                    </Typography>
                                                    {
                                                        ctcae.parentTerm.specifyOther && (
                                                            <Typography variant="caption" component="div">
                                                                {ctcae.specification}
                                                            </Typography>
                                                        )
                                                    }
                                                    <Typography variant="caption" component="div">
                                                        {ctcae.parentTerm.description}
                                                    </Typography>
                                                </CtcaeTooltipBox>
                                            </>
                                        )
                                    }
                                    <Divider />
                                    <CtcaeTooltipBox>
                                        <Typography variant="subtitle1">
                                            {ctcae.term.name.replace(ctcae.category.name + ' - ', '')}
                                        </Typography>
                                        <Typography variant="caption" component="div">
                                            MedDRA Code: <em>{ctcae.term.medDraCode}</em>
                                        </Typography>
                                        {
                                            ctcae.term.specifyOther && (
                                                <Typography variant="caption" component="div">
                                                    {ctcae.specification}
                                                </Typography>
                                            )
                                        }
                                        <Typography variant="caption" component="div">
                                            {ctcae.term.description}
                                        </Typography>
                                    </CtcaeTooltipBox>
                                    {
                                        ctcae.grade && (
                                            <>
                                                <Divider />
                                                <CtcaeTooltipBox>
                                                    <Typography variant="subtitle1">
                                                        Grade {ctcae.grade.name}
                                                    </Typography>
                                                    <Typography variant="caption">
                                                        {ctcae.grade.description}
                                                    </Typography>
                                                </CtcaeTooltipBox>
                                            </>
                                        )
                                    }
                                </CtcaeTooltip>
                            }
                        >
                            <IconButton sx={{borderRadius: 0, padding: 0, marginTop: -1, marginBottom: -1, marginLeft: 2, alignSelf: 'top'}} color="info">
                                <FontAwesomeIcon size="lg" icon={faInfoSquare} />
                            </IconButton>
                        </Tooltip>
                    )
                }
            </Format>
        </Field>
    )
}

export default SourceDataCtcaeValue;